import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import {
  useAppDispatch,
  useAppSelector,
  useDeptProjectName,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { MainForm, RequiredNotification } from '@Components/index';
import {
  getAttendanceReasonsDict,
  getAttendanceTypesDict,
  getCompaniesDictSelector,
  getDepartmentsProjectsDict,
  getProjectNDepartmentsDict,
  getWorkLocationTypesAllowed,
} from '@Store/selectors/dictionarySelector';
import {
  useCancelHrmRequestMutation,
  useDeleteHrmRequestMutation,
  useGetHrmRequestFormQuery,
  useSubmitHrmRequestMutation,
} from '@Store/api/basicHrmApiSlice';
import {
  transformCompaniesDictToOptions,
  transformDeptProjDictToOptions,
  transformDictToOptions,
  transformDictToOptionsNum,
} from '@Utils/transformToOptions';
import { NotificationContext } from '@Context/NotificationContext';
import { FooterActions } from '@Enum/index';
import { useGetProjectsOrDepartmentsMutation } from '@Store/api/dictionaryApiSlice';
import { currentAttendanceLocation } from '@Features/EmployeeSelfService/requests/AttendanceRequest/store/selectors/attendanceRequestSelector';
import { dictionaryActions } from '@Store/slices/dictionarySlice';
import { getLocationSymbol } from '@Utils/getLocationSymbol';
import { getEditFormType } from '@Features/utils/getEditFormType';
import { AttendanceStatusCodes } from '@Enum/api/AttendanceStatusCodes.enum';
import { validateForm } from '@Features/utils/validateForm';

import { getTotalDays } from '@Utils/transformDateTime';
import { attendanceRequestActions } from '../../store/attendanceRequestSlice';
import { usePostAttendanceFormMutation } from '../../store/api/attendanceRequestApiSlice';
import RequestDetailsSection from '../components/RequestDetailsSection';
import { AttendanceRequestFormValues } from '../types';
import { initialValuesForm } from '../initialValuesForm';
import { getAttendanceReqFormSchema } from '../validationScheme';
import { prepareReasonOptions } from '../utils';

const AttendanceRequestEditForm: React.FC = () => {
  const {
    deleteSuccessNotification,
    errorNotification,
    editSuccessNotification,
    submitSuccessNotification,
    cancelSuccessNotification,
  } = useContext(NotificationContext);
  const { requestId } = useParams();
  const { data: editDataForm } = useGetHrmRequestFormQuery(requestId!, {
    refetchOnMountOrArgChange: true,
  });
  const requisitionInfo = useRequisitionInfoExistingReq(
    editDataForm,
    'Attendance'
  );
  const [postAttendanceForm] = usePostAttendanceFormMutation();
  const [deleteRequest] = useDeleteHrmRequestMutation();
  const [cancelRequest] = useCancelHrmRequestMutation();
  const [initialFormValues, setInitialFormValues] = useState(initialValuesForm);
  const [reqType, setReqType] = useState<number | null>(null);
  const [showDeleteNotification, setShowDeleteNotification] = useState(false);
  const [submitRequest] = useSubmitHrmRequestMutation();
  const currentUser = useAppSelector(currentUserSelector);
  const requestTypes = useAppSelector(getAttendanceTypesDict);
  const projectNDepartments = useAppSelector(getProjectNDepartmentsDict);
  const departmentProjects = useAppSelector(getDepartmentsProjectsDict);
  const reasons = useAppSelector(getAttendanceReasonsDict);
  const companies = useAppSelector(getCompaniesDictSelector);
  const workLocationTypes = useAppSelector(getWorkLocationTypesAllowed);
  const currentAttendanceLocationType = useAppSelector(
    currentAttendanceLocation
  );
  const [getProjectsDepartments] = useGetProjectsOrDepartmentsMutation();
  const { findDeptProjName } = useDeptProjectName();
  const dispatch = useAppDispatch();
  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const { handleBackToListRequests } = useNavigationToListRequests();

  useEffect(
    () => () => {
      dispatch(attendanceRequestActions.setAttendanceRequest(null));
    },
    [requestId]
  );

  const handleSubmit = useCallback(
    (values: AttendanceRequestFormValues) => {
      setDisableSaveForm(true);

      const body = {
        requestId: editDataForm?.requestId,
        empNumber: editDataForm?.empNumber as string,
        taReqToCompID: Number(values.taReqToCompID),
        leaveTypeID: values.leaveTypeID,
        deptProjID: values.deptProjID as number,
        totDays: Number(editDataForm?.totDays),
        groupID: Number(values.groupID),
        wlId: values.wlId,
        remarks: values.remarks,
        startDate: values.startDate,
        endDate: values.endDate,
        currentLocation: values.currentLocation,
      };

      postAttendanceForm(body)
        .unwrap()
        .then(({ requestId: reqId, createdDate, reqStatusId }) => {
          dispatch(
            attendanceRequestActions.setAttendanceRequest({
              createdDate,
              requestId: reqId,
              reqStatusId,
            })
          );

          submitRequest(String(reqId))
            .unwrap()
            .then(() => {
              submitSuccessNotification(String(reqId));

              setDisableSaveForm(false);

              handleBackToListRequests();
            })
            .catch(({ data }) => {
              errorNotification(data);
              setDisableSaveForm(false);
            });
        })
        .catch(({ data }) => {
          errorNotification(data);
          setDisableSaveForm(false);
        });
    },
    [handleBackToListRequests, editDataForm]
  );

  const onReqTypeChanged = useCallback(
    (value: number) => {
      setReqType(value);

      setInitialFormValues({
        ...initialValuesForm,
        leaveTypeID: value,
        currentLocation: editDataForm?.currentLocation || '',
      });
    },
    [editDataForm]
  );

  const validationSchema = getAttendanceReqFormSchema(reqType);

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema,
  });

  const handleSave = useCallback(async (): Promise<void> => {
    const isFormValid = await validateForm(validationSchema, formik);

    if (!isFormValid) return;

    setDisableSaveForm(true);

    const body = {
      empNumber: editDataForm?.empNumber as string,
      requestId: editDataForm?.requestId,
      taReqToCompID: Number(formik.values.taReqToCompID),
      leaveTypeID: Number(formik.values.leaveTypeID),
      deptProjID: Number(formik.values.deptProjID),
      groupID: Number(formik.values.groupID),
      totDays: formik.values.totDays,
      remarks: formik.values.remarks,
      currentLocation: formik.values.currentLocation,
      wlId: formik.values.wlId,
      startDate: formik.values.startDate,
      endDate: formik.values.endDate,
    };

    postAttendanceForm(body)
      .unwrap()
      .then(({ createdDate, requestId: reqId, reqStatusId }) => {
        dispatch(
          attendanceRequestActions.setAttendanceRequest({
            createdDate,
            requestId: reqId,
            reqStatusId,
          })
        );
        editSuccessNotification(String(reqId));
        setDisableSaveForm(false);
      })
      .catch(({ data }) => {
        errorNotification(data);
        setDisableSaveForm(false);
      });
  }, [editDataForm, formik, validationSchema]);

  const setWorkLocationType = useCallback((): void => {
    if (typeof editDataForm?.wlId === 'number') {
      const transformSelectedValue = getLocationSymbol(editDataForm.wlId);
      dispatch(
        attendanceRequestActions.setAttendanceLocation(transformSelectedValue)
      );
    }
  }, [editDataForm?.wlId]);

  useEffect(() => {
    if (editDataForm?.empNumber && formik.values.taReqToCompID) {
      setWorkLocationType();
      getProjectsDepartments({
        compId: Number(formik.values.taReqToCompID),
        empNo: editDataForm.empNumber,
        isAdditionalRequest: true,
        workLocation: currentAttendanceLocationType,
      })
        .unwrap()
        .then((response) => {
          dispatch(dictionaryActions.setDepartmentsProjects(response));
        });
    }
  }, [
    editDataForm?.empNumber,
    formik.values.taReqToCompID,
    currentAttendanceLocationType,
    setWorkLocationType,
  ]);

  useEffect(() => {
    if (editDataForm) {
      formik.setValues({
        empNumber: editDataForm.empNumber,
        remarks: editDataForm.remarks,
        wlId: editDataForm.wlId,
        taReqToCompID: String(editDataForm.taReqToCompID),
        leaveTypeID: editDataForm.leaveTypeID,
        deptProjID: editDataForm.deptProjID,
        startDate: editDataForm.startDate,
        endDate: editDataForm.endDate,
        groupID: Number(editDataForm.groupID),
        currentLocation: editDataForm.currentLocation,
        totDays: editDataForm.totDays,
      });
    }
  }, [editDataForm]);

  useEffect(() => {
    if (formik.values.startDate && formik.values.endDate) {
      const totalDays = getTotalDays(
        formik.values.endDate,
        formik.values.startDate
      );

      formik.setFieldValue('totDays', totalDays);
    } else {
      formik.setFieldValue('totDays', 0);
    }
  }, [formik.values.startDate, formik.values.endDate]);

  const mappedProjects = useMemo(() => {
    return projectNDepartments.map((project) => project.projDeptName);
  }, [projectNDepartments]);

  const showDeleteRequestPopup = (): void => {
    setShowDeleteNotification(true);
  };

  const handleDeleteRequest = useCallback((): void => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    deleteRequest(reqId)
      .unwrap()
      .then(() => {
        deleteSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [editDataForm, handleBackToListRequests]);

  const handleCancel = useCallback(() => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    cancelRequest(reqId)
      .unwrap()
      .then(() => {
        cancelSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [handleBackToListRequests, editDataForm]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.CANCEL) {
        handleCancel();
      }

      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSave();
      }

      if (action === FooterActions.DELETE_REQUEST) {
        showDeleteRequestPopup();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSave, handleCancel]
  );

  const isOpenStatus =
    !editDataForm || editDataForm.reqStatusId === AttendanceStatusCodes.OPEN;
  const formType = getEditFormType(isOpenStatus);

  const reasonOptions = prepareReasonOptions(
    reasons,
    formik.values.leaveTypeID
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        formType={formType}
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        disabledSaveForm={disabledSaveForm}
        onFooterAction={handleFooterAction}
      >
        <FormItems>
          <RequestDetailsSection
            label="Request Details"
            formik={formik}
            companies={transformCompaniesDictToOptions(companies)}
            requestTypes={transformDictToOptions(requestTypes)}
            workLocationTypes={transformDictToOptionsNum(workLocationTypes)}
            projectNDepartments={mappedProjects}
            departmentProjects={transformDeptProjDictToOptions(
              departmentProjects
            )}
            reasonsType={reasonOptions}
            findDeptProjName={findDeptProjName}
            onReqTypeChange={onReqTypeChanged}
          />
        </FormItems>
      </MainForm>
      <RequiredNotification
        title="Delete Request"
        message={`Are you sure you want to delete request no.${requestId}?`}
        isModalOpen={showDeleteNotification}
        textSuccessBtn="Yes, Delete"
        textCancelBtn="No, Thanks"
        handleCancel={() => setShowDeleteNotification(false)}
        handleSuccess={handleDeleteRequest}
        isDeleteNotification
      />
    </Flex>
  );
};

export default AttendanceRequestEditForm;
